import { useEffect } from 'react';

/**
 * Left slide-out "operator handbook" — plain-language docs for every facet of the
 * admin console so anyone can be trained to run the platform: managing carriers,
 * accounts, users, and keeping costs in line. Opened from the header; deep-links
 * to the section for the tab you're on.
 */

interface HelpSection {
  id: string;
  title: string;
  blurb: string;
  points: { h: string; d: string }[];
}

const SECTIONS: HelpSection[] = [
  {
    id: 'overview',
    title: 'How the platform works',
    blurb:
      'CommissionSight ingests each carrier’s monthly commission statement, maps it to a unified model, scores every member month-over-month (green = unchanged, yellow = changed, red = dropped), and computes commission owed (your contracted rate vs. what the carrier actually paid) and chargebacks. The control plane (accounts, carriers, configs, billing) lives in one shared database; each account’s member data lives in its own isolated database.',
    points: [
      {
        h: 'The flow',
        d: 'Account is created → its data store is provisioned → a carrier + config is set up → the customer (or you) uploads statements → each upload becomes a job that ingests + scores → results appear in the app and in the admin Jobs tab.',
      },
      {
        h: 'Your job as operator',
        d: 'Onboard carriers correctly, approve + provision accounts, set the right product line + rate schedule so commission owed is computed, watch the Jobs/Logs tabs for failures or mis-scores, and keep costs in line (AI usage, retention, billing).',
      },
    ],
  },
  {
    id: 'metrics',
    title: 'Metrics',
    blurb: 'A live health snapshot of the whole platform.',
    points: [
      {
        h: 'What to watch',
        d: 'Jobs in the last 24h, failures, pending accounts, and pending/failed webhooks. A rising failure count is your first signal something is misconfigured.',
      },
    ],
  },
  {
    id: 'revenue',
    title: 'Revenue',
    blurb: 'What the platform is billing across accounts.',
    points: [
      {
        h: 'Keeping costs in line',
        d: 'Revenue is per active member per month (PMPM). Compare it against your infrastructure + AI spend. If an account’s AI usage (see Accounts → AI usage) is high relative to its revenue, consider a cap or moving them to bring-your-own-key.',
      },
    ],
  },
  {
    id: 'accounts',
    title: 'Accounts',
    blurb:
      'The customer organizations. Expand a row to manage everything about an account: provisioning, processing, entitlements, workspaces, rate schedules, AI usage, and billing.',
    points: [
      {
        h: 'Approve & provision',
        d: 'New accounts start “pending.” Approve them, then provision their isolated data store (auto-creates a database). An active account that isn’t provisioned can’t process uploads — Logs flags this.',
      },
      {
        h: 'Processing toggles',
        d: 'Pause statement processing or AI per account (e.g. non-payment). A paused account’s uploads queue but don’t run.',
      },
      {
        h: 'Feature entitlements',
        d: 'Turn features on/off per account since pricing is case-by-case: workspaces, BYOK AI, API access, and roadmap items. Default off — enable what you’ve sold. Enforced server-side, not just hidden.',
      },
      {
        h: 'Workspaces',
        d: 'Enable multi-workspace for parent/child agency structures; each workspace is scored and (optionally) rate-billed separately.',
      },
      {
        h: 'Rate schedules',
        d: 'The contracted commission rules that drive “commission owed.” See the Carriers + Rate schedules section below — a Medicare/ancillary carrier with no rate schedule computes $0 owed.',
      },
      {
        h: 'AI usage & billing',
        d: 'AI usage shows queries/tokens/cost for cost control. Billing holds the contact + payment method and the per-member rate (custom rate overrides the default).',
      },
    ],
  },
  {
    id: 'users',
    title: 'Users',
    blurb: 'Everyone who can sign in — customer members and back-office admins.',
    points: [
      {
        h: 'Admin access',
        d: 'Admin access is granted ONLY by adding an email to the allowlist (not by the role column). Customers can never reach the admin console.',
      },
      {
        h: 'Impersonate',
        d: 'Open the customer app AS a user — a short-lived, READ-ONLY support session — to see exactly what they see when diagnosing an issue. Every action is audited.',
      },
    ],
  },
  {
    id: 'carriers',
    title: 'Carriers, configs & rate schedules',
    blurb:
      'A carrier needs (1) a config that maps its statement columns, and (2) for Medicare/ancillary, a rate schedule. Get these right and everything downstream just works.',
    points: [
      {
        h: 'Product line — the most important setting',
        d: 'Each carrier config has a product line: Major medical (% premium / flat per member), Medicare (CMS advance-and-earn), or Ancillary (premium-based). If a Medicare carrier is left as “major medical,” the CMS engine never runs and owed is wrong. Onboarding now infers this from the sample — confirm it.',
      },
      {
        h: 'Column mapping',
        d: 'Maps the carrier’s headers to canonical fields (member id, commission amount, effective date, etc.). The onboarding wizard drafts it from a sample file; you review and tweak it in the structured editor (no JSON) — a grid of source column → canonical field, with per-column value transforms (currency/date/percent), privacy controls, and an Advanced section for messy-file rules. Check the effective-date column especially for Medicare.',
      },
      {
        h: 'Rate schedules',
        d: 'For Medicare/ancillary, the rate schedule is what makes “commission owed” computable. Use the structured editor (no JSON): pick the product line, scope it to a workspace/carrier (or leave “all”), and fill in the CMS table / premium bands. No schedule → owed is 0 and the job is flagged.',
      },
      {
        h: 'Medicare base payment cadence',
        d: 'On a Medicare schedule, set Base payment cadence to match how the carrier pays the CMS base: “Annual upfront” (a single lump — the default and most common) or “Monthly” (12 equal installments). Leaving an installment-paying carrier on Annual upfront overstates owed every period (it expects the full annual base against one month’s payment). When unsure, leave it Annual upfront and confirm the cadence with the carrier.',
      },
      {
        h: 'If a statement looks mis-scored',
        d: 'Check the Jobs tab — a completed job carries a warning when the product line, mapping, or rate schedule looks wrong (e.g. “0 of N records owed-evaluable”). Fix the config/schedule and reprocess.',
      },
    ],
  },
  {
    id: 'marketing',
    title: 'Marketing (cold outreach)',
    blurb:
      'Run cold-email campaigns to the ICP lead list — STRICTLY INTERNAL (never exposed to customers or the SDK). It’s human-in-the-loop: the AI writes drafts in your voice, you review and approve each send, and a hard daily cap keeps volume safe.',
    points: [
      {
        h: 'The flow: 1 → 2 → 3',
        d: '1) Create a campaign and Enroll leads. 2) Click “Generate drafts (AI)” to draft a batch (or “Start auto-drafting” to let it run on a schedule). 3) Open each draft in the inbox and click Send. Replies thread back and the AI drafts a response for your approval.',
      },
      {
        h: 'Leads & tiers',
        d: 'The Leads list is your imported ICP targets. “Tier” is lead quality: 0 = best (verified site, matching email, etc.), higher = weaker. Score (0–100) is the ICP fit. “Segment” is the line of business (medicare, health_life, group, ancillary). Click View on a lead to see its campaigns + full message history.',
      },
      {
        h: 'Creating a campaign — the fields',
        d: 'Name = your label. Segment = only enroll leads in that line (or “any”). Max tier = only enroll leads at/above that quality, e.g. 0 means tier-0 only, 2 means tiers 0–2. Daily cap = the most emails this campaign sends per day (1–15; never exceeds 15). Brief = the value-prop/voice the AI leads with.',
      },
      {
        h: 'Enroll',
        d: '“Enroll 50 leads” pulls the best-matching, not-yet-enrolled, non-suppressed leads (by tier then score) into the campaign as “queued.” Click again to add another 50. Enrolling does NOT draft or send anything.',
      },
      {
        h: 'Generate drafts (AI) — drafts a BATCH, not the whole queue',
        d: 'Click it to AI-draft up to the campaign’s daily cap (e.g. 12) from the queued leads — NOT all 50 at once, because sending is capped at ≤15/day anyway. It shows a spinner while it works (one AI call per lead, a few seconds each), then a toast: “Drafted N (M failed).” Click it again to draft the next batch.',
      },
      {
        h: 'Auto-drafting (optional)',
        d: '“Start auto-drafting” makes a background job top up the draft queue to the daily cap every ~15 minutes, so you don’t have to click Generate. “Pause auto-drafting” stops it (existing drafts stay). You can rely on either the button or auto-drafting — both produce drafts you still approve.',
      },
      {
        h: 'Lead status (the inbox)',
        d: 'queued = enrolled, nothing drafted yet · drafted = an AI draft is waiting for your approval · sent = you approved & it went out · replied = the prospect responded (AI drafted a reply for you) · unsubscribed = they opted out (auto-suppressed, never contacted again).',
      },
      {
        h: 'The counts on each campaign row',
        d: 'The four numbers are lead counts by status — queued / drafted / sent / replied. So “0 queued · 12 drafted · 3 sent · 1 replied” means 12 drafts await your approval, 3 already sent, 1 reply came back.',
      },
      {
        h: 'Edit or regenerate a draft before sending',
        d: 'Every AI draft in a thread has three actions: Edit (tweak the subject/body inline), ↻ Regenerate (discard it and have the AI write a fresh one — a reply draft if the prospect wrote back, otherwise a new cold open), and Send. Only drafts are editable; once sent, a message is locked.',
      },
      {
        h: 'What the AI writes (rotating angles)',
        d: 'Cold drafts rotate across the platform’s value angles — underpayment recovery, attrition/churn, chargeback tracing, multi-carrier reconciliation, statement tie-out, Ask-AI, time saved — picking one that fits the lead’s segment so it’s relevant, and varying it across the batch. Replies may include a single link to commissionsight.com/features when a prospect wants to see more.',
      },
      {
        h: 'Sending (approve)',
        d: 'Open a thread, review the AI draft (Edit or Regenerate if needed), and click Send (spinner while sending). Sending is capped per day per campaign — past the cap, Send is refused until tomorrow, and a contact who unsubscribed is refused too. Every send BCCs you, uses Reply-To so responses route back, and includes a one-click unsubscribe header + footer link.',
      },
      {
        h: 'Replies & opt-outs',
        d: 'When a prospect replies, it appears in the thread (green) and the AI drafts a demo-seeking response for your approval. If they ask to unsubscribe/stop, they’re auto-suppressed and excluded from all future enrollment — no reply is drafted.',
      },
    ],
  },
  {
    id: 'jobs',
    title: 'Jobs',
    blurb:
      'Every statement upload becomes a job: ingest + score. This is where you diagnose problems.',
    points: [
      {
        h: 'Statuses',
        d: 'queued → processing → completed / failed. A failed job shows the error; a completed job may still carry health warnings.',
      },
      {
        h: 'Reprocess vs. rescore',
        d: 'Retry re-reads the file with the current config (use after fixing a mapping/product line). Rescore re-runs the delta/owed engine on already-ingested data without re-reading the file (use after fixing a rate schedule or an out-of-order baseline).',
      },
      {
        h: 'Exceptions',
        d: 'Rejected rows are written to a downloadable exception file so you can see exactly which rows failed and why.',
      },
    ],
  },
  {
    id: 'logs',
    title: 'Logs & alerts',
    blurb:
      'A time-ordered feed of jobs, webhook deliveries + inbound replies, plus active alerts you can acknowledge.',
    points: [
      {
        h: 'Alerts to act on',
        d: 'Failed jobs, jobs stuck in processing, exhausted webhook deliveries, active-but-unprovisioned accounts, and — important — “mis-scored” / “zero owed coverage” alerts that catch a Medicare/ancillary carrier that isn’t computing owed.',
      },
      {
        h: 'Clearing alerts',
        d: 'Alerts are computed live from current state. Use the ✕ on an alert (or “Clear all”) to acknowledge and hide it; “Restore N cleared” brings them back. A genuinely new problem gets a new id and reappears, so clearing won’t permanently mask a recurring issue.',
      },
      {
        h: 'Inbound replies',
        d: 'Prospect replies to cold outreach also surface in the feed (source “reply”), alongside ingest jobs and webhook deliveries.',
      },
    ],
  },
  {
    id: 'system',
    title: 'System (maintenance)',
    blurb: 'The scheduled (cron) jobs that keep things healthy.',
    points: [
      {
        h: 'What runs',
        d: 'Re-enqueue stuck jobs, redeliver pending webhooks, purge raw statement files per the retention policy (cost control), run metered billing, and sweep for owed-coverage gaps.',
      },
    ],
  },
  {
    id: 'costs',
    title: 'Keeping costs in line',
    blurb: 'Where platform cost comes from and how to manage it.',
    points: [
      {
        h: 'AI',
        d: 'The biggest variable cost. Watch Accounts → AI usage; set a monthly cap, enable pass-through billing, or move heavy users to bring-your-own-key (BYOK) so they pay their own provider.',
      },
      {
        h: 'Storage / retention',
        d: 'Raw statement files are auto-purged after processing (24h) and exception files after 30 days, so storage stays bounded — the System cron handles this.',
      },
      {
        h: 'Per-account data stores',
        d: 'Each account has its own database. A pending account that never activates still costs nothing material; but don’t provision accounts you won’t onboard.',
      },
    ],
  },
];

export function HelpDrawer({
  open,
  onClose,
  section,
}: {
  open: boolean;
  onClose: () => void;
  section?: string;
}) {
  // Esc closes; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Scroll to the section for the current tab when opened.
  useEffect(() => {
    if (!open || !section) return;
    const t = setTimeout(() => {
      document.getElementById(`help-${section}`)?.scrollIntoView({ block: 'start' });
    }, 60);
    return () => clearTimeout(t);
  }, [open, section]);

  return (
    <>
      {/* backdrop (click or Enter/Space to close) */}
      <button
        type="button"
        aria-label="Close help"
        tabIndex={-1}
        onClick={onClose}
        className={`fixed inset-0 z-40 cursor-default bg-neutral-900/40 transition-opacity ${open ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      />
      {/* left panel */}
      <aside
        aria-label="Operator handbook"
        className={`fixed inset-y-0 left-0 z-50 flex w-full max-w-md flex-col bg-white shadow-2xl transition-transform duration-300 ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <h2 className="text-base font-semibold">Operator handbook</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-900"
            aria-label="Close help"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <nav className="flex flex-wrap gap-1.5 border-b border-neutral-100 px-5 py-3">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#help-${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(`help-${s.id}`)?.scrollIntoView({ block: 'start' });
              }}
              className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-200"
            >
              {s.title.split(' ')[0]}
            </a>
          ))}
        </nav>
        <div className="flex-1 space-y-7 overflow-y-auto px-5 py-5">
          {SECTIONS.map((s) => (
            <section key={s.id} id={`help-${s.id}`} className="scroll-mt-2">
              <h3 className="text-sm font-bold uppercase tracking-wide text-emerald-700">
                {s.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{s.blurb}</p>
              <dl className="mt-3 space-y-2.5">
                {s.points.map((p) => (
                  <div
                    key={p.h}
                    className="rounded-lg border border-neutral-200/70 bg-neutral-50 p-3"
                  >
                    <dt className="text-sm font-semibold text-neutral-800">{p.h}</dt>
                    <dd className="mt-0.5 text-sm leading-relaxed text-neutral-600">{p.d}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </aside>
    </>
  );
}
